import { FaPoll, FaPlus, FaEdit, FaTrash, FaEnvelope, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaClock, FaEye, FaFileAlt, FaChevronDown, FaChevronUp, FaCopy, FaUsers, FaExclamationTriangle } from "react-icons/fa";
import { useState } from "react";
import { useAuth } from "./AuthContext";

export default function Elections() {
  const { user } = useAuth();
  const isAdmin = user?.isProfileAdmin || false;

  // Election stages
  const STAGES = {
    PLANNING: "Planning",
    NOMINATIONS_OPEN: "Nominations Open",
    NOMINATIONS_CLOSED: "Nominations Closed",
    CONFIRMING_WILLINGNESS: "Confirming Willingness",
    COLLECTING_STATEMENTS: "Collecting Statements",
    ELECTION_SETUP: "Election Setup",
    VOTING_OPEN: "Voting Open",
    VOTING_CLOSED: "Voting Closed",
    RESULTS_ANNOUNCED: "Results Announced",
    COMPLETED: "Completed"
  };

  const [elections, setElections] = useState([
    {
      id: 1,
      title: "Staff Council Representatives - Spring 2026",
      type: "Election",
      stage: STAGES.CONFIRMING_WILLINGNESS,
      positions: [
        {
          id: 1,
          title: "Provost Office Representative",
          constituency: "Provost Office",
          candidates: [
            {
              id: 1,
              name: "Nils Lewis",
              email: "nl2345@nyu.edu",
              nominatedBy: "Self-nominated",
              willingnessConfirmed: true,
              willingnessDate: "2026-01-05",
              statementReceived: false,
              statementDueDate: "2026-01-12",
            }
          ]
        },
        {
          id: 2,
          title: "External Relations Representative",
          constituency: "External Relations",
          candidates: [
            {
              id: 2,
              name: "Furat Al-Samaraie",
              email: "fa3456@nyu.edu",
              nominatedBy: "Colleague nomination",
              willingnessConfirmed: false,
              willingnessDate: null,
              statementReceived: false,
              statementDueDate: "2026-01-12",
            }
          ]
        }
      ],
      nominationsOpenDate: "2026-01-03",
      nominationsCloseDate: "2026-01-10",
      votingOpenDate: "2026-01-15",
      votingCloseDate: "2026-01-22",
      heliosLink: "https://vote.heliosvoting.org/",
      notes: "Remember to inform SCSC Chair and Communications Committee Chair",
    }
  ]);

  const [selectedElection, setSelectedElection] = useState(null);
  const [showAddElection, setShowAddElection] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [expandedPosition, setExpandedPosition] = useState(null);
  const [showAddCandidate, setShowAddCandidate] = useState(null);
  const [candidateForm, setCandidateForm] = useState({
    name: "",
    email: "",
    nominatedBy: "",
  });

  // Email Templates
  const emailTemplates = {
    callForNominations: {
      subject: "Call for Nominations - Staff Council Representatives",
      body: `Dear Colleagues,

This is an official call for nominations for representatives on the Staff Council, for the following vacancies:

• Office of the Vice Chancellor Representative
• Provost Office Representative
• External Relations Representative
• Administrative & Business Operations Representative
• Research Institute Representative
• At-Large Representative

If you wish to nominate yourself or a colleague, please email us at nyuad.staffcouncilelections@nyu.edu. The nominations period will remain open until 5pm on [DATE].

Please note that as per Staff Governance By-Laws, those nominated may decline the nomination without reproach.

If you have any questions, or wish to confirm the department of yourself or a colleague, please reach out to us at nyuad.staffcouncilelections@nyu.edu.

Regards,
Staff Council`
    },
    reminderNominations: {
      subject: "Reminder: Call for Nominations - Staff Council Representatives",
      body: `Dear Colleagues,

This is a reminder of the official call for nominations for representatives on the Staff Council, for the following vacancies:

[SAME AS ABOVE, with updated deadline]`
    },
    confirmWillingness: {
      subject: "Confirmation of Nomination - [POSITION]",
      body: `Dear [NAME],

You have been nominated to stand in the election for the [POSITION] on Staff Council.

We sincerely hope you are willing to accept this nomination. If you are, please send us a candidate statement, introducing yourself and outlining your motivations to stand no later than 5pm on [DATE].

For reference, here [link] is a sample statement from a previous election.

Please note that you may decline this nomination without reproach, as per clause 2.4 of the voting procedure.

If you have any questions please contact me directly or the team alias: nyuad.staffcouncilelections@nyu.edu.

Kind regards,
Staff Council`
    },
    requestStatement: {
      subject: "Candidate Statement Request - [POSITION]",
      body: `Dear [NAME],

We (Staff Council) are delighted to hear you are interested in standing for [POSITION]. To confirm your candidacy, please send us a candidate statement, introducing yourself and outlining your motivations to stand no later than 5pm on [DATE].

For reference, I have attached some candidate statements from previous elections.

If you have any questions, please contact me directly or the team alias: nyuad.staffcouncilelections@nyu.edu.

Kind regards,
Staff Council`
    },
    selfNominationConfirm: {
      subject: "Confirmation of Self-Nomination - [POSITION]",
      body: `Dear [NAME],

Thank you for your self-nomination for [POSITION] on Staff Council.

To confirm your candidacy, please send us a candidate statement, introducing yourself and outlining your motivations to stand no later than 5pm on [DATE].

For reference, I have attached some candidate statements from previous elections.

If you have any questions, please contact me directly or the team alias: nyuad.staffcouncilelections@nyu.edu.

Kind regards,
Staff Council`
    },
    resultsToSCSC: {
      subject: "Election Results - [ELECTION TITLE]",
      body: `Dear SCSC,

We are pleased to share with you the results of our recent elections:

[POSITION 1] - [WINNER NAME]
[POSITION 2] - [WINNER NAME]

We plan to announce these results to the staff body at-large [DATE].

Thank you for your ongoing support.

Kind regards,
Staff Council`
    },
    resultsToJessicaMartin: {
      subject: "Election Results - [ELECTION TITLE]",
      body: `Dear Jessica & Martin,

We are pleased to share with you the results of our recent elections:

Admin Staff:
• [POSITION] - [NAME]

Researchers:
• [POSITION] - [NAME]

Academic Staff:
• [POSITION] - [NAME]

We plan to announce these results to the staff body at-large [DATE].

Thank you for your ongoing support.

Kind regards,
Staff Council`
    },
    successfulCandidate: {
      subject: "Election Results - Congratulations!",
      body: `Dear [NAME],

Thank you for your participation in the election for the [POSITION] position on the Staff Council.

The election for this position ended yesterday, and I am pleased to tell you that you were elected.

All candidates will be informed of the election results this afternoon. Tomorrow, we will email the departments of elected candidates informing them of their chosen representatives.

Thank you so much for your enthusiasm and initiative, and I hope to see you soon. Please keep an eye out for an email regarding a short orientation in the next few days.

Kind regards,
Staff Council`
    },
    unsuccessfulCandidate: {
      subject: "Election Results - [POSITION]",
      body: `Dear [NAME],

Staff Council regrets to inform you that you were not elected [POSITION].

An announcement will be sent out to the wider faculty tomorrow, but I wanted to let you know in advance.

Staff Council very much appreciates your candidacy and we sincerely hope that you continue to consider future involvement in faculty governance efforts.

Regards,
Staff Council`
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Email template copied to clipboard!");
  };

  const getStageColor = (stage) => {
    const colors = {
      [STAGES.PLANNING]: "#a0aec0",
      [STAGES.NOMINATIONS_OPEN]: "#4299e1",
      [STAGES.NOMINATIONS_CLOSED]: "#9f7aea",
      [STAGES.CONFIRMING_WILLINGNESS]: "#ed8936",
      [STAGES.COLLECTING_STATEMENTS]: "#f6ad55",
      [STAGES.ELECTION_SETUP]: "#48bb78",
      [STAGES.VOTING_OPEN]: "#38b2ac",
      [STAGES.VOTING_CLOSED]: "#667eea",
      [STAGES.RESULTS_ANNOUNCED]: "#805ad5",
      [STAGES.COMPLETED]: "#48bb78",
    };
    return colors[stage] || "#a0aec0";
  };

  const getCandidateStatus = (candidate) => {
    if (!candidate.willingnessConfirmed) return { label: "Awaiting Confirmation", color: "#ed8936" };
    if (!candidate.statementReceived) return { label: "Awaiting Statement", color: "#f6ad55" };
    return { label: "Ready for Election", color: "#48bb78" };
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const handleAddCandidate = (positionId) => {
    if (!candidateForm.name || !candidateForm.email) {
      alert("Please fill in Name and Email");
      return;
    }

    setElections(elections.map(election => {
      if (election.id === selectedElection.id) {
        return {
          ...election,
          positions: election.positions.map(pos => {
            if (pos.id === positionId) {
              return {
                ...pos,
                candidates: [
                  ...pos.candidates,
                  {
                    id: Math.max(0, ...pos.candidates.map(c => c.id)) + 1,
                    ...candidateForm,
                    willingnessConfirmed: false,
                    willingnessDate: null,
                    statementReceived: false,
                    statementDueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  }
                ]
              };
            }
            return pos;
          })
        };
      }
      return election;
    }));

    setCandidateForm({ name: "", email: "", nominatedBy: "" });
    setShowAddCandidate(null);
  };

  const updateCandidateStatus = (positionId, candidateId, field, value) => {
    setElections(elections.map(election => {
      if (election.id === selectedElection.id) {
        return {
          ...election,
          positions: election.positions.map(pos => {
            if (pos.id === positionId) {
              return {
                ...pos,
                candidates: pos.candidates.map(candidate => {
                  if (candidate.id === candidateId) {
                    const updates = { [field]: value };
                    if (field === 'willingnessConfirmed' && value) {
                      updates.willingnessDate = new Date().toISOString().split('T')[0];
                    }
                    return { ...candidate, ...updates };
                  }
                  return candidate;
                })
              };
            }
            return pos;
          })
        };
      }
      return election;
    }));
  };

  const deleteCandidate = (positionId, candidateId) => {
    if (!window.confirm("Are you sure you want to remove this candidate?")) return;

    setElections(elections.map(election => {
      if (election.id === selectedElection.id) {
        return {
          ...election,
          positions: election.positions.map(pos => {
            if (pos.id === positionId) {
              return {
                ...pos,
                candidates: pos.candidates.filter(c => c.id !== candidateId)
              };
            }
            return pos;
          })
        };
      }
      return election;
    }));
  };

  const updateElectionStage = (electionId, newStage) => {
    setElections(elections.map(e => 
      e.id === electionId ? { ...e, stage: newStage } : e
    ));
    if (selectedElection && selectedElection.id === electionId) {
      setSelectedElection({ ...selectedElection, stage: newStage });
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: "40px", maxWidth: "800px", margin: "0 auto", textAlign: "center" }}>
        <div style={{ background: "white", borderRadius: "12px", padding: "60px 40px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)", border: "1px solid #e2e8f0" }}>
          <FaPoll style={{ fontSize: "64px", color: "#cbd5e0", marginBottom: "20px" }} />
          <h2 style={{ fontSize: "24px", fontWeight: "600", color: "#2d3748", marginBottom: "12px" }}>
            Access Restricted
          </h2>
          <p style={{ color: "#718096", fontSize: "16px", lineHeight: "1.6" }}>
            Election management is only accessible to administrators.
          </p>
        </div>
      </div>
    );
  }

  // Election List View
  if (!selectedElection) {
    return (
      <div style={{ padding: "40px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ fontSize: "32px", fontWeight: "700", color: "#2d3748", marginBottom: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
              <FaPoll style={{ color: "#667eea" }} />
              Elections & Referenda
            </h1>
            <p style={{ color: "#718096", fontSize: "16px", margin: 0 }}>
              Manage election processes and track candidate progress
            </p>
          </div>
          <button
            onClick={() => setShowEmailTemplates(!showEmailTemplates)}
            style={{
              padding: "12px 24px",
              background: "#f7fafc",
              color: "#667eea",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginRight: "12px",
            }}
          >
            <FaFileAlt /> Email Templates
          </button>
        </div>

        {/* Email Templates Panel */}
        {showEmailTemplates && (
          <div style={{ background: "white", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)", border: "1px solid #e2e8f0" }}>
            <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", marginBottom: "20px" }}>
              Email Templates
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "12px" }}>
              {Object.entries(emailTemplates).map(([key, template]) => (
                <div key={key} style={{ padding: "16px", background: "#f7fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                    {template.subject}
                  </div>
                  <button
                    onClick={() => copyToClipboard(template.body)}
                    style={{
                      width: "100%",
                      padding: "8px",
                      background: "#667eea",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    <FaCopy /> Copy Template
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Elections List */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {elections.map(election => (
            <div
              key={election.id}
              onClick={() => setSelectedElection(election)}
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                border: "1px solid #e2e8f0",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.08)";
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "16px" }}>
                <div>
                  <h3 style={{ fontSize: "20px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                    {election.title}
                  </h3>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <span style={{
                      padding: "4px 12px",
                      background: getStageColor(election.stage),
                      color: "white",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}>
                      {election.stage}
                    </span>
                    <span style={{ fontSize: "13px", color: "#718096" }}>
                      {election.positions.length} position{election.positions.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", fontSize: "13px", color: "#718096" }}>
                <div>
                  <strong>Nominations:</strong> {new Date(election.nominationsOpenDate).toLocaleDateString()} - {new Date(election.nominationsCloseDate).toLocaleDateString()}
                </div>
                <div>
                  <strong>Voting:</strong> {new Date(election.votingOpenDate).toLocaleDateString()} - {new Date(election.votingCloseDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Election Detail View
  return (
    <div style={{ padding: "40px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "30px" }}>
        <button
          onClick={() => setSelectedElection(null)}
          style={{
            padding: "8px 16px",
            background: "#f7fafc",
            color: "#4a5568",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            marginBottom: "16px",
          }}
        >
          ← Back to Elections List
        </button>
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#2d3748", marginBottom: "8px" }}>
              {selectedElection.title}
            </h1>
            <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "12px" }}>
              <span style={{
                padding: "6px 14px",
                background: getStageColor(selectedElection.stage),
                color: "white",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
              }}>
                {selectedElection.stage}
              </span>
            </div>
          </div>
          
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => setShowEmailTemplates(!showEmailTemplates)}
              style={{
                padding: "10px 20px",
                background: "#f7fafc",
                color: "#667eea",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FaEnvelope /> Email Templates
            </button>
          </div>
        </div>
      </div>

      {/* Stage Progress */}
      <div style={{ background: "white", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)", border: "1px solid #e2e8f0" }}>
        <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#2d3748", marginBottom: "16px" }}>
          Election Progress
        </h2>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {Object.values(STAGES).map((stage) => (
            <button
              key={stage}
              onClick={() => updateElectionStage(selectedElection.id, stage)}
              style={{
                padding: "8px 16px",
                background: selectedElection.stage === stage ? getStageColor(stage) : "#f7fafc",
                color: selectedElection.stage === stage ? "white" : "#4a5568",
                border: `1px solid ${selectedElection.stage === stage ? getStageColor(stage) : "#e2e8f0"}`,
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {stage}
            </button>
          ))}
        </div>
      </div>

      {/* Email Templates (if open) */}
      {showEmailTemplates && (
        <div style={{ background: "white", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)", border: "1px solid #e2e8f0" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#2d3748", marginBottom: "16px" }}>
            Quick Access Email Templates
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "12px" }}>
            {Object.entries(emailTemplates).map(([key, template]) => (
              <div key={key} style={{ padding: "12px", background: "#f7fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#2d3748", marginBottom: "8px" }}>
                  {template.subject.replace('[POSITION]', '').replace('[ELECTION TITLE]', '')}
                </div>
                <button
                  onClick={() => copyToClipboard(template.body)}
                  style={{
                    width: "100%",
                    padding: "6px",
                    background: "#667eea",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  <FaCopy /> Copy
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Dates */}
      <div style={{ background: "white", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)", border: "1px solid #e2e8f0" }}>
        <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#2d3748", marginBottom: "16px" }}>
          Key Dates & Information
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
          <div>
            <div style={{ fontSize: "12px", color: "#a0aec0", marginBottom: "4px" }}>Nominations Open</div>
            <div style={{ fontSize: "14px", color: "#2d3748", fontWeight: "500" }}>
              {new Date(selectedElection.nominationsOpenDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#a0aec0", marginBottom: "4px" }}>Nominations Close</div>
            <div style={{ fontSize: "14px", color: "#2d3748", fontWeight: "500" }}>
              {new Date(selectedElection.nominationsCloseDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#a0aec0", marginBottom: "4px" }}>Voting Opens</div>
            <div style={{ fontSize: "14px", color: "#2d3748", fontWeight: "500" }}>
              {new Date(selectedElection.votingOpenDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "12px", color: "#a0aec0", marginBottom: "4px" }}>Voting Closes</div>
            <div style={{ fontSize: "14px", color: "#2d3748", fontWeight: "500" }}>
              {new Date(selectedElection.votingCloseDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </div>
        
        {selectedElection.heliosLink && (
          <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0" }}>
            <a
              href={selectedElection.heliosLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                color: "#667eea",
                fontSize: "14px",
                fontWeight: "500",
                textDecoration: "none",
              }}
            >
              🔗 Open Helios Voting Platform →
            </a>
          </div>
        )}
        
        {selectedElection.notes && (
          <div style={{ marginTop: "16px", padding: "12px", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "8px" }}>
            <div style={{ display: "flex", alignItems: "start", gap: "8px" }}>
              <FaExclamationTriangle style={{ color: "#ed8936", marginTop: "2px", flexShrink: 0 }} />
              <div style={{ fontSize: "13px", color: "#744210" }}>
                <strong>Important:</strong> {selectedElection.notes}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Positions & Candidates */}
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {selectedElection.positions.map((position) => (
          <div
            key={position.id}
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* Position Header */}
            <div
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", cursor: "pointer" }}
              onClick={() => setExpandedPosition(expandedPosition === position.id ? null : position.id)}
            >
              <div>
                <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#2d3748", marginBottom: "4px" }}>
                  {position.title}
                </h3>
                <div style={{ fontSize: "13px", color: "#718096" }}>
                  {position.constituency} • {position.candidates.length} candidate{position.candidates.length !== 1 ? 's' : ''}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddCandidate(position.id);
                    setCandidateForm({ name: "", email: "", nominatedBy: "" });
                  }}
                  style={{
                    padding: "8px 16px",
                    background: "#667eea",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <FaPlus /> Add Candidate
                </button>
                {expandedPosition === position.id ? <FaChevronUp /> : <FaChevronDown />}
              </div>
            </div>

            {/* Add Candidate Form */}
            {showAddCandidate === position.id && (
              <div style={{ marginBottom: "20px", padding: "16px", background: "#f7fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748", marginBottom: "12px" }}>
                  Add New Candidate
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                  <input
                    type="text"
                    placeholder="Candidate Name"
                    value={candidateForm.name}
                    onChange={(e) => setCandidateForm({ ...candidateForm, name: e.target.value })}
                    style={{ padding: "8px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={candidateForm.email}
                    onChange={(e) => setCandidateForm({ ...candidateForm, email: e.target.value })}
                    style={{ padding: "8px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <input
                    type="text"
                    placeholder="Nominated By"
                    value={candidateForm.nominatedBy}
                    onChange={(e) => setCandidateForm({ ...candidateForm, nominatedBy: e.target.value })}
                    style={{ padding: "8px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "13px" }}
                  />
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => handleAddCandidate(position.id)}
                    style={{
                      padding: "8px 16px",
                      background: "#48bb78",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    Save Candidate
                  </button>
                  <button
                    onClick={() => setShowAddCandidate(null)}
                    style={{
                      padding: "8px 16px",
                      background: "#f7fafc",
                      color: "#4a5568",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Candidates List */}
            {expandedPosition === position.id && (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {position.candidates.length === 0 ? (
                  <div style={{ padding: "20px", textAlign: "center", color: "#a0aec0", fontSize: "14px" }}>
                    No candidates yet. Click "Add Candidate" to get started.
                  </div>
                ) : (
                  position.candidates.map((candidate) => {
                    const status = getCandidateStatus(candidate);
                    return (
                      <div
                        key={candidate.id}
                        style={{
                          padding: "16px",
                          background: "#f7fafc",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }}>
                          <div>
                            <div style={{ fontSize: "15px", fontWeight: "600", color: "#2d3748", marginBottom: "4px" }}>
                              {candidate.name}
                            </div>
                            <div style={{ fontSize: "13px", color: "#718096", marginBottom: "4px" }}>
                              {candidate.email}
                            </div>
                            <div style={{ fontSize: "12px", color: "#a0aec0" }}>
                              Nominated by: {candidate.nominatedBy}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{
                              padding: "4px 10px",
                              background: status.color,
                              color: "white",
                              borderRadius: "6px",
                              fontSize: "11px",
                              fontWeight: "600",
                            }}>
                              {status.label}
                            </span>
                            <button
                              onClick={() => deleteCandidate(position.id, candidate.id)}
                              style={{
                                padding: "6px",
                                background: "#fff5f5",
                                color: "#f56565",
                                border: "1px solid #feb2b2",
                                borderRadius: "6px",
                                fontSize: "12px",
                                cursor: "pointer",
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>

                        {/* Candidate Status Checkboxes */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={candidate.willingnessConfirmed}
                              onChange={(e) => updateCandidateStatus(position.id, candidate.id, 'willingnessConfirmed', e.target.checked)}
                              style={{ cursor: "pointer" }}
                            />
                            <span style={{ color: candidate.willingnessConfirmed ? "#48bb78" : "#718096" }}>
                              <FaCheckCircle style={{ marginRight: "4px" }} />
                              Willingness Confirmed
                            </span>
                            {candidate.willingnessDate && (
                              <span style={{ fontSize: "11px", color: "#a0aec0" }}>
                                ({new Date(candidate.willingnessDate).toLocaleDateString()})
                              </span>
                            )}
                          </label>

                          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={candidate.statementReceived}
                              onChange={(e) => updateCandidateStatus(position.id, candidate.id, 'statementReceived', e.target.checked)}
                              style={{ cursor: "pointer" }}
                            />
                            <span style={{ color: candidate.statementReceived ? "#48bb78" : isOverdue(candidate.statementDueDate) ? "#f56565" : "#718096" }}>
                              <FaFileAlt style={{ marginRight: "4px" }} />
                              Statement Received
                            </span>
                            {candidate.statementDueDate && !candidate.statementReceived && (
                              <span style={{ fontSize: "11px", color: isOverdue(candidate.statementDueDate) ? "#f56565" : "#a0aec0" }}>
                                (Due: {new Date(candidate.statementDueDate).toLocaleDateString()})
                              </span>
                            )}
                          </label>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Process Checklist */}
      <div style={{ marginTop: "20px", background: "white", borderRadius: "12px", padding: "24px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)", border: "1px solid #e2e8f0" }}>
        <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#2d3748", marginBottom: "16px" }}>
          Process Reminders
        </h2>
        <div style={{ fontSize: "13px", color: "#4a5568", lineHeight: "1.8" }}>
          <div style={{ marginBottom: "8px" }}>✓ Nominations open for <strong>5 working days</strong> (can be extended)</div>
          <div style={{ marginBottom: "8px" }}>✓ Willingness to serve: <strong>3 days after nominations close</strong></div>
          <div style={{ marginBottom: "8px" }}>✓ Candidate statements: <strong>3 days after nominations close</strong></div>
          <div style={{ marginBottom: "8px" }}>✓ Send reminder emails before deadlines</div>
          <div style={{ marginBottom: "8px" }}>✓ Keep SCSC Chair and Communications Committee Chair informed</div>
          <div style={{ marginBottom: "8px" }}>✓ Email Jessica Sederquist and Martin Klimke with results</div>
          <div>✓ Email successful candidates before unsuccessful ones</div>
        </div>
      </div>
    </div>
  );
}
